import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Link2, Link2Off, Loader2, RefreshCw, Save } from "lucide-react";
import { api } from "@/lib/api";
import { ignoreToText, textToIgnore } from "@/lib/prospectsView";
import { tr, uiLocale } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

/**
 * Settings → Sales inbox (Prospects).
 *
 * Every user connects ONE mailbox per property (Microsoft 365 or Google)
 * and picks their PublishOS sales rep for this property. A mailbox can
 * belong to only one property. Connecting bounces to the provider and
 * back to /settings?mail_status=..., flashed here as a toast.
 *
 * Admins also see everyone's mailboxes on this property and edit the
 * notes the AI uses when it drafts (sales notes, website, ignore list,
 * prompts).
 */
export default function SalesInboxSection() {
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const setup = useQuery({ queryKey: ["prospects-setup"], queryFn: api.prospectsSetup, refetchOnMount: true });
  const data = setup.data;
  const conn = data?.connection;

  const [repId, setRepId] = useState("");
  const [signature, setSignature] = useState("");
  const [start, setStart] = useState("new");

  useEffect(() => {
    if (conn) {
      setRepId(conn.rep_id || "");
      setSignature(conn.signature || "");
      setStart(conn.start || "new");
    }
  }, [conn]);

  // Preselect the rep whose email is the mailbox's, the first time.
  useEffect(() => {
    if (conn && !conn.rep_id && data?.reps?.length) {
      const same = data.reps.find((r) => (r.email || "").toLowerCase() === conn.address);
      if (same) setRepId(same.id);
    }
  }, [conn, data]);

  useEffect(() => {
    const s = sp.get("mail_status");
    if (!s) return;
    const msg = sp.get("mail_msg") || "";
    if (s === "connected") {
      toast.success(tr("Mailbox connected{value}", { value: msg ? ` — ${msg}` : "" }));
      qc.invalidateQueries({ queryKey: ["prospects-setup"] });
    } else if (s === "denied") {
      toast.warning(tr("You cancelled the mailbox connection ({msg})", { msg }));
    } else {
      toast.error(tr("Mailbox connection failed — {msg}", { msg }), { duration: 12000 });
    }
    const clean = new URLSearchParams(sp);
    clean.delete("mail_status");
    clean.delete("mail_msg");
    setSp(clean, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = useMutation({
    mutationFn: (provider) => api.prospectsAuthUrl(provider),
    onSuccess: ({ url }) => { window.location.href = url; },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Could not start the connection")),
  });
  const save = useMutation({
    mutationFn: () => {
      const rep = (data?.reps || []).find((r) => r.id === repId);
      const patch = { rep_id: repId, rep_name: rep?.name || "", signature };
      if (!conn.synced_once) patch.start = start;
      return api.prospectsUpdateConnection(conn.id, patch);
    },
    onSuccess: () => { toast.success(tr("Saved")); qc.invalidateQueries({ queryKey: ["prospects-setup"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Couldn't save")),
  });
  const sync = useMutation({
    mutationFn: () => api.prospectsSyncNow(conn.id),
    onSuccess: (r) => {
      if (r?.error) toast.error(r.error);
      else if (r?.skipped) toast.message(tr("Already checking — try again in a minute"));
      else toast.success(tr("Checking in the background. New prospects show up in Prospects within a few minutes."));
      qc.invalidateQueries({ queryKey: ["prospects-setup"] });
      qc.invalidateQueries({ queryKey: ["prospects-counts"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Check failed")),
  });
  const disconnect = useMutation({
    mutationFn: () => api.prospectsDeleteConnection(conn.id),
    onSuccess: () => { toast.success(tr("Mailbox disconnected")); qc.invalidateQueries({ queryKey: ["prospects-setup"] }); },
  });

  if (setup.isLoading || !data) {
    return (
      <div className="text-xs text-[var(--ink-muted)] flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />{" "}{tr("Checking…")}</div>
    );
  }

  const connectButtons = (
    <div className="flex flex-wrap items-center gap-2">
      {[["ms", "Microsoft 365"], ["google", "Google"]].map(([p, label]) => (
        <button
          key={p}
          type="button"
          data-testid={`sales-inbox-connect-${p}`}
          onClick={() => connect.mutate(p)}
          disabled={!data.apps?.[p] || !data.key_configured || connect.isPending}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-[var(--slate-900)] text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {connect.isPending && connect.variables === p ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
          {conn ? tr("Reconnect with {label}", { label }) : tr("Connect {label}", { label })}
        </button>
      ))}
    </div>
  );

  return (
    <div data-testid="sales-inbox-section">
      <p className="text-xs text-[var(--ink-muted)] mb-3">{tr("Connect the mailbox where ad prospects write to you on this property. Newsdesk reads it every two minutes, keeps only the emails that look like ad prospects, and drafts replies for you to send from the Prospects page. It never moves, deletes or marks your mail as read. Each property needs its own mailbox.")}</p>

      {!data.key_configured && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1.5 mb-3">{tr("MAIL_TOKEN_KEY is not set on the server yet, so mailboxes can't be connected.")}</div>
      )}
      {(!data.apps?.ms || !data.apps?.google) && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1.5 mb-3">
          {!data.apps?.ms && tr("Microsoft 365 app keys are not set on the server. ")}
          {!data.apps?.google && tr("Google app keys are not set on the server.")}
        </div>
      )}

      {!conn ? (
        connectButtons
      ) : (
        <div className="space-y-4">
          <div
            data-testid="sales-inbox-connected"
            className={`flex flex-wrap items-center gap-2 text-xs px-2.5 py-1.5 border ${
              conn.status === "ok"
                ? "text-emerald-800 bg-emerald-50 border-emerald-200"
                : "text-amber-800 bg-amber-50 border-amber-200"
            }`}
          >
            <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
            <span className="flex-1">
              {conn.address} ({conn.provider_label})
              {conn.status === "ok"
                ? conn.last_sync_at ? tr(" · last checked {value}", { value: new Date(conn.last_sync_at).toLocaleString(uiLocale()) }) : tr(" · not checked yet")
                : ` · ${conn.error || tr("needs attention")}`}
            </span>
          </div>

          <label className="block">
            {trRich("<0>Your PublishOS sales rep on this property</0>", null, [<span className="overline block mb-1.5" />])}
            {data.publishos ? (
              <select
                data-testid="sales-inbox-rep"
                value={repId}
                onChange={(e) => setRepId(e.target.value)}
                className="w-full sm:w-80 border border-[var(--border)] bg-white px-2 py-1.5 text-sm"
              >
                <option value="">{tr("Choose…")}</option>
                {(data.reps || []).map((r) => (
                  <option key={r.id} value={r.id}>{r.name}{r.email ? ` (${r.email})` : ""}</option>
                ))}
              </select>
            ) : (
              <span className="text-xs text-[var(--ink-muted)]">{tr("This property isn't linked to PublishOS.")}</span>
            )}
            {data.reps_error && <span className="block text-xs text-red-700 mt-1">{tr("PublishOS: {reps_error}", { reps_error: data.reps_error })}</span>}
            {trRich("<0>New clients created from your emails are assigned to this rep, and your emails are logged under them.</0>", null, [<span className="block text-[11px] text-[var(--ink-muted)] mt-1" />])}
          </label>

          <label className="block">
            {trRich("<0>Signature</0>", null, [<span className="overline block mb-1.5" />])}
            <textarea
              data-testid="sales-inbox-signature"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              rows={4}
              placeholder={tr("Oliver Quintero\nGringo Gazette North\n+52 …")}
              className="w-full border border-[var(--border)] px-3 py-2 text-sm leading-relaxed outline-none focus:border-[var(--slate-900)] bg-white"
            />
            {trRich("<0>Added under every reply sent from Newsdesk (your mail app's own signature isn't added by the API).</0>", null, [<span className="block text-[11px] text-[var(--ink-muted)] mt-1" />])}
          </label>

          {!conn.synced_once && (
            <fieldset className="text-xs">
              {trRich("<0>Start with</0>", null, [<span className="overline block mb-1.5" />])}
              <label className="flex items-center gap-2 mb-1">
                <input type="radio" checked={start === "new"} onChange={() => setStart("new")} />{tr("New mail only, from when you connected")}</label>
              <label className="flex items-center gap-2">
                <input type="radio" checked={start === "14d"} onChange={() => setStart("14d")} />{tr("Also look back over the last 14 days (once)")}</label>
            </fieldset>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="sales-inbox-save"
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-50"
            >
              {save.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{tr("Save")}</button>
            <button
              type="button"
              data-testid="sales-inbox-sync"
              onClick={() => sync.mutate()}
              disabled={sync.isPending || conn.status === "reconnect"}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-[var(--border-strong)] hover:bg-[var(--secondary)] disabled:opacity-50"
            >
              {sync.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}{tr("Check now")}</button>
            <button
              type="button"
              data-testid="sales-inbox-disconnect"
              onClick={() => {
                if (window.confirm(tr("Disconnect this mailbox? Its conversations stay in Prospects."))) disconnect.mutate();
              }}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-[var(--border-strong)] hover:bg-[var(--secondary)]"
            >
              <Link2Off className="w-3.5 h-3.5" />{" "}{tr("Disconnect")}</button>
          </div>
          {connectButtons}
        </div>
      )}

      <p className="text-[11px] text-[var(--ink-muted)] mt-3 font-mono-display break-all">{tr("Redirect URIs: {ms} · {google}", { ms: data.redirects?.ms, google: data.redirects?.google })}
      </p>

      {data.is_admin && <AdminProspectSettings connections={data.all_connections || []} />}
    </div>
  );
}

function AdminProspectSettings({ connections }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["prospects-settings"], queryFn: api.prospectsSettings });
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (q.data && draft === null) {
      setDraft({ ...q.data, ignore_text: ignoreToText(q.data.ignore) });
    }
  }, [q.data, draft]);

  const save = useMutation({
    mutationFn: () => api.prospectsSaveSettings({
      sales_notes: draft.sales_notes,
      sales_website: draft.sales_website,
      never_promise: draft.never_promise,
      ignore: textToIgnore(draft.ignore_text),
      classify_prompt: draft.classify_prompt,
      reply_prompt: draft.reply_prompt,
      follow_up_days: Number(draft.follow_up_days) || 3,
    }),
    onSuccess: (saved) => {
      toast.success(tr("Sales settings saved"));
      setDraft({ ...draft, ...saved, ignore_text: ignoreToText(saved.ignore) });
      qc.invalidateQueries({ queryKey: ["prospects-settings"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Couldn't save")),
  });

  const set = (k, v) => setDraft({ ...draft, [k]: v });
  const field = (k, label, rows, hint, placeholder) => (
    <label className="block mb-3">
      <span className="overline block mb-1.5">{label}</span>
      <textarea
        data-testid={`prospect-settings-${k}`}
        value={draft[k] || ""}
        onChange={(e) => set(k, e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full border border-[var(--border)] px-3 py-2 text-sm leading-relaxed outline-none focus:border-[var(--slate-900)] bg-white"
      />
      {hint && <span className="block text-[11px] text-[var(--ink-muted)] mt-1">{hint}</span>}
    </label>
  );

  return (
    <div className="pt-4 mt-4 border-t border-[var(--border)]">
      <h3 className="font-mono-display text-[11px] uppercase tracking-wider text-[var(--ink-muted)] mb-2">{tr("Everyone's mailboxes on this property (admin)")}</h3>
      {connections.length === 0 ? (
        <p className="text-xs text-[var(--ink-muted)] mb-4">{tr("No one has connected a mailbox yet.")}</p>
      ) : (
        <div className="mb-4 border border-[var(--border)] divide-y divide-[var(--border)]">
          {connections.map((c) => (
            <div key={c.id} className="px-3 py-2 text-xs flex flex-wrap gap-x-3 gap-y-1" data-testid={`sales-inbox-row-${c.id}`}>
              {trRich("<0>{user_name}</0><1>{address} ({provider_label})</1><2>rep: {rep_name}</2><3>{value}</3>", { user_name: c.user_name || c.user_email, address: c.address, provider_label: c.provider_label, rep_name: c.rep_name || tr("not set"), value: c.status === "ok" ? (c.last_sync_at ? tr("checked {value}", { value: new Date(c.last_sync_at).toLocaleString(uiLocale()) }) : tr("not checked yet")) : c.error || c.status }, [<span className="font-medium" />, <span />, <span className="text-[var(--ink-muted)]" />, <span className={c.status === "ok" ? "text-emerald-800" : "text-amber-800"} />])}
            </div>
          ))}
        </div>
      )}

      <h3 className="font-mono-display text-[11px] uppercase tracking-wider text-[var(--ink-muted)] mb-2">{tr("What the AI knows when it drafts (admin)")}</h3>
      {!draft ? (
        <div className="text-xs text-[var(--ink-muted)] flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />{" "}{tr("Loading…")}</div>
      ) : (
        <>
          {field("sales_notes", tr("About the paper"), 5,
            tr("Facts the reply may use: circulation, where it's distributed, audience, print deadlines. Prices come from PublishOS, not from here."),
            tr("12,000 copies every two weeks across Rosarito, Ensenada and Tijuana…"))}
          <label className="block mb-3">
            {trRich("<0>Sales website (optional)</0>", null, [<span className="overline block mb-1.5" />])}
            <input
              data-testid="prospect-settings-sales_website"
              value={draft.sales_website || ""}
              onChange={(e) => set("sales_website", e.target.value)}
              placeholder="https://store.gringogazette.com"
              className="w-full sm:w-96 border border-[var(--border)] px-3 py-1.5 text-sm bg-white outline-none focus:border-[var(--slate-900)]"
            />
          </label>
          {field("never_promise", tr("Never promise"), 2, tr("Things the AI must never offer (it says the rep will check instead)."),
            tr("Front page placement, discounts, guaranteed article coverage…"))}
          {field("ignore_text", tr("Ignore these senders"), 3,
            tr("One address or domain per line. Never treated as prospects. \"Not a prospect\" with \"ignore this sender\" adds to this list."))}
          <label className="block mb-3">
            {trRich("<0>Default follow-up (days after a reply)</0>", null, [<span className="overline block mb-1.5" />])}
            <input
              data-testid="prospect-settings-follow_up_days"
              type="number"
              min={1}
              max={30}
              value={draft.follow_up_days ?? 3}
              onChange={(e) => set("follow_up_days", e.target.value)}
              className="w-24 border border-[var(--border)] px-2 py-1.5 text-sm bg-white"
            />
          </label>
          <details className="mb-3">
            <summary className="text-xs cursor-pointer text-[var(--ink-muted)]">{tr("Prompts (advanced)")}</summary>
            <div className="mt-3">
              {field("classify_prompt", tr("Is it a prospect? (blank = default)"), 6, null, draft.default_classify_prompt)}
              {field("reply_prompt", tr("How to write the reply (blank = default)"), 10,
                tr("{paper} becomes the property name. The offer, the thread and the price and link rules are always added."),
                draft.default_reply_prompt)}
            </div>
          </details>
          <button
            type="button"
            data-testid="prospect-settings-save"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-50"
          >
            {save.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{tr("Save sales settings")}</button>
        </>
      )}
    </div>
  );
}
